<script setup lang="ts">
import { resolveImg } from '@/composables/useGameImages'
import { COMMON_ASSETS } from '@/commonAssets'
import { computed, ref, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { fetchBalance } from '../api/wallets'
import { ApiError } from '../api/http'
import { findCurrency } from '../config/currencies'
import TintIcon from './TintIcon.vue'

const props = defineProps<{
  gameId: string
  modalId: string
  token?: string
  walletIcon?: string
  /** walletIcon 染系統色（用共用 RULE_BUTTONS.wallet 的遊戲）。 */
  tintWalletIcon?: boolean
  walletLabel?: string
  walletLabelEn?: string
  // 来自 v1/{gameCode}/bets 的币种代码（例如 "CNY"）
  currency?: string
  // 来自 v1/{gameCode}/bets 的币种名称（例如 "人民幣"）
  currencyName?: string
}>()

const emit = defineEmits<{
  close: [modalId: string]
  authInvalid: []
}>()

const { t, locale } = useI18n()

const balance = ref<number>(0)
const loading = ref(true)
const error = ref<string | null>(null)

const walletLabel = computed(() => {
  if (locale.value === 'zh') {
    return props.walletLabel || t('balance.walletLabel')
  }
  return props.walletLabelEn || props.walletLabel || t('balance.walletLabel')
})
// 文字主題色：用 Tailwind utility `text-theme`（colors.theme 在 tailwind.config.js 定義）

// ========== 币种符号 ==========
// 直接用 host 端硬编码的币种表（src/config/currencies.ts）按 code 查符号，
// 不再依赖 shared-web / 各游戏的 currencies 配置。
const currencySymbol = computed(() => findCurrency(props.currency)?.symbol || '')

const loadBalance = async () => {
  loading.value = true
  error.value = null

  if (!props.token) {
    error.value = t('common.errors.missingLogin')
    loading.value = false
    return
  }

  try {
    // token 由 useHttp 自動從 authStore 帶上；401 會由 http 攔截器 invalidate
    const data = await fetchBalance()
    balance.value = data.balance ?? 0
  } catch (e: unknown) {
    if (e instanceof ApiError && e.status === 401) {
      emit('authInvalid')
      error.value = t('common.errors.tokenExpired')
    } else if (e instanceof ApiError && e.status === 0) {
      error.value = t('common.errors.networkError')
    } else {
      error.value = t('common.errors.fetchFailed')
    }
  } finally {
    loading.value = false
  }
}

const formatWithComma = (value: number) => {
  return new Intl.NumberFormat(locale.value, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

// van-popup 顯示狀態：mount 後翻 true 播放上滑 enter；關閉時翻 false 播 leave，
// 等 van-popup `closed`（leave 動畫結束）才 emit close 讓 App.vue 卸載，動畫不被切。
const show = ref(false)
const requestClose = () => {
  show.value = false
}
const onClosed = () => {
  emit('close', props.modalId)
}

onMounted(() => {
  loadBalance()
  void nextTick(() => {
    show.value = true
  })
})
</script>

<template>
  <!-- 改用 vant van-popup（同歷史/規則：teleport 到 body + overlay-frame-clamp 寬度吃 --card-w）。
       position=bottom 提供上滑動畫；內層 .balance-frame 維持原本 48vh 半高 sheet 外觀。 -->
  <van-popup
    :show="show"
    position="bottom"
    :overlay="true"
    overlay-class="overlay-frame-clamp"
    :close-on-click-overlay="true"
    :safe-area-inset-bottom="true"
    :style="{ background: 'transparent', maxWidth: 'none' }"
    @update:show="(v: boolean) => (show = v)"
    @closed="onClosed"
  >
    <!-- 底部面板（寬度吃 --card-w 對齊卡片；48vh 半高） -->
    <div
      class="balance-frame relative mx-auto h-[48vh] rounded-t-[0.8rem] bg-[rgb(40,40,50)] pb-6 shadow-[0_-8px_24px_rgba(0,0,0,0.25)]"
    >
      <!-- 标题栏 -->
      <div class="h-22 mx-auto flex w-[95%] items-center justify-between">
        <!-- 左侧占位 -->
        <div class="w-16" />
        <!-- 标题 -->
        <div class="h-22 leading-22 my-4 text-center font-normal text-white">
          <p class="text-[1.6rem] font-medium">{{ t('balance.title') }}</p>
        </div>
        <!-- 关闭按钮 -->
        <img
          :src="resolveImg(COMMON_ASSETS.iconClose)"
          :alt="t('rules.mainGame.items.close.title')"
          draggable="false"
          @click="requestClose"
          class="scale-85 my-4 mr-2 size-14 cursor-pointer opacity-70"
        />
      </div>

      <!-- 加载状态 -->
      <div v-if="loading" class="flex flex-col items-center justify-center gap-4 py-16">
        <div class="flex gap-2">
          <span class="modal-dot modal-dot-1"></span>
          <span class="modal-dot modal-dot-2"></span>
          <span class="modal-dot modal-dot-3"></span>
        </div>
        <span class="text-sm text-theme">{{ t('modal.loading') }}</span>
      </div>

      <!-- 错误状态 -->
      <div
        v-else-if="error"
        class="flex flex-col items-center justify-center gap-4 py-16 text-white/80"
      >
        <span>{{ error }}</span>
        <button
          class="cursor-pointer rounded border border-white/40 bg-transparent px-6 py-2 text-white/90 hover:bg-white/10"
          @click="loadBalance"
        >
          {{ t('balance.retry') }}
        </button>
      </div>

      <!-- 钱包卡片 -->
      <div
        v-else
        class="bg-[rgb(48, 48, 60)] mx-auto mt-[0.6rem] flex h-[4.7rem] w-[95%] items-center justify-between rounded-[0.8rem] text-[1.3rem]"
        style="background-color: rgb(48, 48, 60)"
      >
        <div class="flex items-center gap-3 pl-[1.9rem] font-medium">
          <TintIcon
            v-if="props.walletIcon && props.tintWalletIcon"
            :src="resolveImg(props.walletIcon)"
            width="2rem"
            class="h-8 w-8"
          />
          <img
            v-else-if="props.walletIcon"
            :src="resolveImg(props.walletIcon)"
            :alt="walletLabel"
            class="h-8 w-8 object-contain"
          />
          <span class="text-theme">{{ walletLabel }}</span>
        </div>
        <div class="flex items-center gap-1 pr-[2.4rem] tracking-wide text-theme">
          <!-- 币种符号；没有则不渲染 -->
          <span v-if="currencySymbol">{{ currencySymbol }}</span>
          <span>{{ formatWithComma(balance) }}</span>
        </div>
      </div>
    </div>
  </van-popup>
</template>

<style scoped>
/* 寬度吃 --card-w（跟 .popup-frame / overlay-frame-clamp 同源 → 對齊 Cocos 卡片）；
 * var() 失效退回 CSS 9:16 公式。margin auto 在 van-popup（position=bottom 全寬）內置中。 */
.balance-frame {
  width: var(--card-w, min(100vw, calc(100dvh * 9 / 16)));
}
</style>
